/**
 * The warnings, folded (user, 19/10/2026: "it's really ugly"): one amber
 * line says how many things to check and names the kinds — "Drops outside
 * the window (3) · Overlapping schedule (4) · +6 more" — and opens on click
 * into the grouped lines (same-code warnings collapse into "<headline> (n):
 * names — suffix"; rare codes keep their sentence). Notes that are not
 * faults (a container that does not run on this weekday) stay a muted line.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { AlertTriangle, CalendarOff, type LucideIcon } from 'lucide-react';

import type { DayPlanPreview } from '../../../drop-generator.types';
import { TogglePill } from '../section/SectionToggle';
import { type GroupedLine, groupWarnings } from './group-warnings';

const Line: FC<{ icon: LucideIcon; iconClass: string; line: GroupedLine }> = ({ icon: Icon, iconClass, line }) => (
  <span className="flex items-start gap-1.5">
    <Icon className={`mt-0.5 h-3 w-3 shrink-0 ${iconClass}`} />
    <span>
      <span className="font-semibold">
        {line.headline}
        {line.names.length > 1 ? ` (${line.names.length})` : ''}:
      </span>{' '}
      {line.names.join(', ')} — {line.suffix}
    </span>
  </span>
);

/** "Drops outside the window (3) · Overlapping schedule (4) · +6 more". */
const kinds = (grouped: GroupedLine[], rest: number): string => {
  const named = grouped.slice(0, 3).map((l) => `${l.headline}${l.names.length > 1 ? ` (${l.names.length})` : ''}`);
  const more = grouped.length - named.length + rest;
  return [...named, more > 0 ? `+${more} more` : null].filter(Boolean).join(' · ');
};

export const TimelineWarnings: FC<{ preview: DayPlanPreview }> = ({ preview }) => {
  const [open, setOpen] = useState(false);
  if (preview.warnings.length === 0) return null;
  const { grouped, notes, rest } = groupWarnings(preview);
  const count = grouped.length + rest.length;
  return (
    <>
      {notes.length > 0 ? (
        <div className="flex flex-col gap-1.5 border-b border-border/50 bg-muted/20 px-5 py-2.5 text-xs text-muted-foreground" data-testid="timeline-notes">
          {notes.map((line) => (
            <Line key={line.headline} icon={CalendarOff} iconClass="text-muted-foreground" line={line} />
          ))}
        </div>
      ) : null}
      {count > 0 ? (
        <div className="border-b border-warning/40 bg-warning/10 text-xs" data-testid="timeline-warnings">
          <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="group flex w-full items-center gap-2 px-5 py-2.5 text-left transition-colors hover:bg-warning/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            <span className="font-semibold">{count} thing{count === 1 ? '' : 's'} to check</span>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{kinds(grouped, rest.length)}</span>
            <TogglePill open={open} className="border-warning/40 bg-warning/5" />
          </button>
          {open ? (
            <div className="flex flex-col gap-1.5 px-5 pb-3 duration-200 animate-in fade-in-0 motion-reduce:animate-none">
              {grouped.map((line) => (
                <Line key={line.headline} icon={AlertTriangle} iconClass="text-warning" line={line} />
              ))}
              {rest.map((warning, i) => (
                <span key={`r${i}`} className="flex items-start gap-1.5">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
                  {warning.message}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
};
